import * as React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Edit, Info, Sparkles, X, Zap } from 'lucide-react';

import { Spinner } from '@/components/ui/spinner';
import { ApiError } from '@/lib/api/client';
import { useScoreHistoryQuery, type ScoreHistoryItem } from '@/lib/api/readiness-scorecard';
import { cn } from '@/lib/utils/cn';

/**
 * T-0116 / E08-F03 — Criterion scoring history modal (`data-open-crit-history`).
 *
 * Visual contract: `design_handoff_caliber_v2/prototype/modals-d3.js`
 * → `window.Modals.openCritHistory`. Vertical timeline (latest first) of
 * every event recorded against one readiness score — AI base-formation
 * proposals, re-evaluations, consultant overrides.
 *
 * Backed by `GET /api/v1/engagements/:id/readiness/scores/:score_id/history`
 * (T-0085). Returns an interleaved stream of audit_log entries +
 * override_records keyed by created_at.
 *
 * Score values
 * ------------
 * Audit `before` / `after` payloads carry the raw register-entry row
 * before/after the write — we extract the `payload.score` field when it's
 * a number or string. Override rows carry the structured
 * `previous_value` / `new_value`. The modal renders any value it can find
 * and falls back to "—" when the shape is unfamiliar (defensive — never
 * crash on a JSON surprise).
 */

export interface CriterionHistoryModalProps {
  engagementId: string;
  scoreId: string | null;
  /** Criterion name + id rendered in the modal header. */
  criterionName?: string;
  criterionId?: string;
  /** Optional current committed score for the header summary line. */
  currentScore?: number | string | null;
  /** Optional AI baseline score for the header summary line. */
  aiBaselineScore?: number | string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CriterionHistoryModal({
  engagementId,
  scoreId,
  criterionName,
  criterionId,
  currentScore,
  aiBaselineScore,
  open,
  onOpenChange,
}: CriterionHistoryModalProps) {
  const query = useScoreHistoryQuery(engagementId, scoreId ?? undefined, { enabled: open });
  const events = query.data?.history ?? [];

  // History is server-sorted ASC (oldest first); the prototype renders
  // newest first so the recent context lands at the top of the drawer.
  const ordered = React.useMemo(() => [...events].reverse(), [events]);

  const titleId = scoreId ? `crit-history-title-${scoreId}` : 'crit-history-title';
  const notFound = query.error instanceof ApiError && query.error.status === 404;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            'fixed inset-0 z-40 bg-scrim',
            'opacity-0 motion-safe:transition-opacity motion-safe:duration-150',
            'data-[state=open]:opacity-100',
          )}
        />
        <Dialog.Content
          aria-labelledby={titleId}
          aria-describedby={undefined}
          className={cn(
            'fixed left-1/2 top-1/2 z-50 grid w-[min(680px,94vw)] max-h-[88vh]',
            '-translate-x-1/2 -translate-y-1/2 grid-rows-[auto,1fr,auto]',
            'overflow-hidden rounded-modal bg-paper-0 shadow-modal',
            'opacity-0 motion-safe:transition-opacity motion-safe:duration-150',
            'data-[state=open]:opacity-100',
          )}
        >
          <Header
            titleId={titleId}
            name={criterionName}
            criterionId={criterionId}
            count={events.length}
            currentScore={currentScore}
            aiBaselineScore={aiBaselineScore}
          />
          <div className="overflow-y-auto bg-paper-50 px-6 py-5">
            {query.isLoading ? (
              <div className="flex items-center gap-2 text-meta text-ink-500">
                <Spinner /> Loading history…
              </div>
            ) : notFound ? (
              <EmptyState
                title="Score not found"
                description="This score's history could not be found. The score may have been deleted, or it belongs to another engagement."
              />
            ) : query.error ? (
              <EmptyState
                title="Could not load history"
                description="The history could not be loaded. Close and reopen to try again."
              />
            ) : ordered.length === 0 ? (
              <EmptyState
                title="No history yet"
                description="This score has no recorded events. Once a consultant overrides or a re-eval runs, every event will land here with its actor and rationale."
              />
            ) : (
              <ol className="space-y-3" aria-label="Score history timeline">
                {ordered.map((ev) => (
                  <li key={timelineKey(ev)} className="relative">
                    <HistoryEvent event={ev} />
                  </li>
                ))}
              </ol>
            )}
            {ordered.length > 0 ? (
              <p className="mt-4 text-center text-meta text-ink-300">— end of history —</p>
            ) : null}
          </div>
          <Footer />
          <Dialog.Close
            aria-label="Close criterion history"
            className={cn(
              'absolute right-4 top-4 inline-flex h-8 w-8 items-center justify-center rounded-card',
              'text-ink-500 hover:bg-paper-100 hover:text-ink-900',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-300',
            )}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function Header({
  titleId,
  name,
  criterionId,
  count,
  currentScore,
  aiBaselineScore,
}: {
  titleId: string;
  name: string | undefined;
  criterionId: string | undefined;
  count: number;
  currentScore: number | string | null | undefined;
  aiBaselineScore: number | string | null | undefined;
}) {
  return (
    <div className="border-b border-ink-100 bg-paper-50 px-6 py-4 pr-14">
      <p className="text-meta uppercase tracking-wide text-ink-500">Override history</p>
      <Dialog.Title
        id={titleId}
        className="mt-1 flex items-baseline gap-2 text-h2 font-semibold text-ink-900"
      >
        {criterionId ? (
          <span className="font-mono text-meta text-ink-500">{criterionId}</span>
        ) : null}
        <span>{name ?? 'Criterion history'}</span>
      </Dialog.Title>
      <p className="mt-1 text-meta text-ink-500">
        Current score <strong className="text-ink-900">{renderScore(currentScore)}</strong>
        {' · '}
        AI baseline <strong className="text-ink-900">{renderScore(aiBaselineScore)}</strong>
        {' · '}
        {count} event{count === 1 ? '' : 's'} on record
      </p>
    </div>
  );
}

function HistoryEvent({ event }: { event: ScoreHistoryItem }) {
  const ts = new Date(event.created_at).toISOString().replace('T', ' ').slice(0, 16);
  if (event.kind === 'override') {
    return (
      <div className="rounded-card border-l-[3px] border-l-accent-amber-600 border-r border-y border-r-ink-100 border-y-ink-100 bg-paper-0 p-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Edit className="h-3.5 w-3.5 text-accent-amber-600" aria-hidden="true" />
            <span className="text-body-sm font-medium text-ink-900">
              Override by <span className="font-mono">{shortActor(event.actor_id)}</span>
            </span>
          </div>
          <span className="font-mono text-meta text-ink-500">{ts}</span>
        </div>
        <div className="mt-1 flex items-center gap-2">
          <span className="rounded-chip bg-accent-amber-100 px-2 py-0.5 text-[10px] font-semibold text-accent-amber-700">
            {event.reason_tag}
          </span>
          <ScoreDiff from={pickValue(event.previous_value)} to={pickValue(event.new_value)} />
        </div>
        <p className="mt-2 text-body-sm leading-relaxed text-ink-700">{event.rationale}</p>
      </div>
    );
  }
  const inferredKind = inferAuditKind(event.event_type);
  return (
    <div className="rounded-card border border-ink-100 bg-paper-0 p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {inferredKind === 'reeval' ? (
            <Sparkles className="h-3.5 w-3.5 text-ai-ink" aria-hidden="true" />
          ) : inferredKind === 'ai' ? (
            <Zap className="h-3.5 w-3.5 text-ai-ink" aria-hidden="true" />
          ) : (
            <Edit className="h-3.5 w-3.5 text-ink-500" aria-hidden="true" />
          )}
          <span className="text-body-sm font-medium text-ink-900">
            {inferredKind === 'ai'
              ? 'AI base-formation'
              : inferredKind === 'reeval'
                ? 'AI re-evaluation'
                : `Audit · ${event.event_type}`}
          </span>
        </div>
        <span className="font-mono text-meta text-ink-500">{ts}</span>
      </div>
      <div className="mt-1">
        <ScoreDiff
          from={pickAuditScore(event.payload.before)}
          to={pickAuditScore(event.payload.after)}
        />
      </div>
      <p className="mt-2 text-body-sm leading-relaxed text-ink-700">
        Actor <span className="font-mono text-meta">{shortActor(event.actor_id)}</span>
        {' · '}
        operation <span className="font-mono text-meta">{event.event_type}</span>
      </p>
    </div>
  );
}

function ScoreDiff({ from, to }: { from: number | string | null; to: number | string | null }) {
  if (from === null && to === null) return null;
  return (
    <div className="flex items-center gap-2 text-meta">
      <span className="text-ink-500">Score</span>
      {from !== null ? (
        <>
          <span className="font-mono text-ink-700">{from}</span>
          <span className="text-ink-500">→</span>
        </>
      ) : null}
      <span className="rounded-chip bg-paper-100 px-2 py-0.5 font-mono text-ink-900">
        {to ?? '—'}
      </span>
    </div>
  );
}

function EmptyState({ title, description }: { title: string; description: string }) {
  return (
    <div className="rounded-card border border-dashed border-ink-200 bg-paper-0 p-5">
      <h3 className="text-h3 font-semibold text-ink-900">{title}</h3>
      <p className="mt-1 text-body-sm leading-relaxed text-ink-700">{description}</p>
    </div>
  );
}

function Footer() {
  return (
    <div className="flex items-center justify-between gap-3 border-t border-ink-100 bg-paper-50 px-6 py-3">
      <div className="flex items-center gap-2 text-meta text-ink-500">
        <Info className="h-3 w-3" aria-hidden="true" />
        Every event is written to the audit trail. Re-evaluations preserve the original AI proposal.
      </div>
      <Dialog.Close
        className={cn(
          'inline-flex h-9 items-center rounded-card border border-ink-200 bg-paper-0 px-3 text-body-sm font-medium text-ink-700',
          'hover:bg-paper-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-300',
        )}
      >
        Close
      </Dialog.Close>
    </div>
  );
}

function timelineKey(ev: ScoreHistoryItem): string {
  return ev.kind === 'override' ? `o-${ev.override_record_id}` : `a-${ev.audit_id}`;
}

function shortActor(actorId: string | null): string {
  if (!actorId) return 'system';
  // Render first 8 characters of a UUID — enough to disambiguate without
  // revealing the full id at a glance. The full id is in the audit row.
  return actorId.slice(0, 8);
}

function pickValue(v: unknown): number | string | null {
  if (typeof v === 'number') return v;
  if (typeof v === 'string') return v;
  if (v && typeof v === 'object') {
    const score = (v as Record<string, unknown>).score;
    if (typeof score === 'number' || typeof score === 'string') return score;
  }
  return null;
}

function pickAuditScore(payload: unknown): number | string | null {
  if (!payload || typeof payload !== 'object') return null;
  // Audit before/after shapes follow `RegisterEntry`-like rows; reach
  // into payload.score when present.
  const top = payload as Record<string, unknown>;
  if (typeof top.score === 'number' || typeof top.score === 'string') {
    return top.score as number | string;
  }
  const nested = top.payload;
  if (nested && typeof nested === 'object') {
    const inner = nested as Record<string, unknown>;
    if (typeof inner.score === 'number' || typeof inner.score === 'string') {
      return inner.score as number | string;
    }
  }
  return null;
}

function inferAuditKind(eventType: string): 'ai' | 'reeval' | 'edit' {
  if (/proposal|extract|base[-_]?formation/i.test(eventType)) return 'ai';
  if (/reeval|re_?evaluat/i.test(eventType)) return 'reeval';
  return 'edit';
}

function renderScore(v: number | string | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return String(v);
}
